import { Dexie } from "dexie"
import { type Observer, type Subscribable, type Unsubscribable } from "dexie"
import * as React from "react"

// ---------------------------------------------------------------------------
// usePromise — React.use() or Suspense-compatible fallback
// ---------------------------------------------------------------------------

const usePromise: <T>(promise: PromiseLike<T>) => T =
  React.use ?? fallbackUsePromise

function fallbackUsePromise<T>(promise: PromiseLike<T>): T {
  const state = PROMISE_STATE_MAP.get(promise)

  if (!state) {
    PROMISE_STATE_MAP.set(promise, { status: "pending" })
    promise.then(
      (value) => {
        PROMISE_STATE_MAP.set(promise, { status: "fulfilled", value })
      },
      (reason) => {
        PROMISE_STATE_MAP.set(promise, { status: "rejected", reason })
      },
    )
    throw promise
  }

  switch (state.status) {
    case "pending":
      throw promise
    case "rejected":
      throw state.reason
    case "fulfilled":
      return state.value as T
  }
}

const PROMISE_STATE_MAP = new WeakMap<
  PromiseLike<unknown>,
  PromiseSettledResult<unknown> | { status: "pending" }
>()

// ---------------------------------------------------------------------------
// useSuspendingObservable — Core observable engine with caching + optimistic
// ---------------------------------------------------------------------------

const observableCache = new Map<React.DependencyList, Subscribable<unknown>>()
const promiseCache = new WeakMap<Subscribable<unknown>, Promise<unknown>>()
const valueCache = new WeakMap<Subscribable<unknown>, unknown>()

const CLEANUP_DELAY = 3000

/**
 * Subscribe to an observable with Suspense support and optimistic updates.
 * Same cache key reuses the same multicast observable.
 */
export function useSuspendingObservable<T>(
  getObservable: (() => Subscribable<T>) | Subscribable<T>,
  cacheKey: React.DependencyList,
): [T, React.Dispatch<React.SetStateAction<T | null>>] {
  let observable: Subscribable<T> | undefined

  // Find existing observable for this cache key
  for (const [key, value] of observableCache) {
    if (
      key.length === cacheKey.length &&
      key.every((k, i) => Object.is(k, cacheKey[i]))
    ) {
      observable = value as Subscribable<T>
      break
    }
  }

  // Create new multicast observable if not found
  if (!observable) {
    const source =
      typeof getObservable === "function" ? getObservable() : getObservable
    let subscription: Unsubscribable | undefined
    const observers = new Set<Observer<T>>()
    let timeout: ReturnType<typeof setTimeout> | undefined

    const newObservable: Subscribable<T> = {
      subscribe: (observer) => {
        observers.add(observer)
        if (timeout != null) {
          clearTimeout(timeout)
          timeout = undefined
        }

        if (!subscription) {
          subscription = source.subscribe({
            next: (val) => {
              valueCache.set(newObservable, val)
              for (const obs of new Set(observers)) obs.next?.(val)
            },
            error: (err) => {
              const lastObservers = new Set(observers)
              handleFinalize()
              for (const obs of lastObservers) obs.error?.(err)
            },
            complete: () => {
              const lastObservers = new Set(observers)
              handleFinalize()
              for (const obs of lastObservers) obs.complete?.()
            },
          })
        } else if (valueCache.has(newObservable)) {
          observer.next?.(valueCache.get(newObservable) as T)
        }

        return {
          unsubscribe: () => {
            if (!observers.has(observer)) return
            observers.delete(observer)
            if (observers.size === 0) scheduleCleanup()
          },
        }

        function handleFinalize() {
          subscription = undefined
          observers.clear()
          valueCache.delete(newObservable)
          promiseCache.delete(newObservable)
          scheduleCleanup()
        }

        function scheduleCleanup() {
          if (timeout != null) return
          timeout = setTimeout(() => {
            subscription?.unsubscribe()
            subscription = undefined
            for (const [key, value] of observableCache) {
              if (value === newObservable) {
                observableCache.delete(key)
                break
              }
            }
          }, CLEANUP_DELAY)
        }
      },
    }

    observable = newObservable
    observableCache.set(cacheKey, newObservable as Subscribable<unknown>)
  }

  // Get or initialize promise for first value
  let promise = promiseCache.get(observable as Subscribable<unknown>)
  if (!promise) {
    const obs = observable
    promise = new Promise<T>((resolve, reject) => {
      const sub = obs.subscribe({
        next: (val) => {
          resolve(val)
          queueMicrotask(() => sub.unsubscribe())
        },
        error: (err) => reject(err),
      })
    })
    promiseCache.set(observable as Subscribable<unknown>, promise)
  }

  const initialValue = usePromise(promise) as T

  const value = React.useRef<T>(initialValue)
  const [error, setError] = React.useState<unknown>()
  const rerender = React.useReducer((x) => x + 1, 0)[1]
  const [optimistic, setOptimistic] = React.useState<T | null>(null)

  value.current = valueCache.has(observable as Subscribable<unknown>)
    ? (valueCache.get(observable as Subscribable<unknown>) as T)
    : initialValue

  React.useEffect(() => {
    const sub = observable!.subscribe({
      next: (val) => {
        setOptimistic(null)
        if (!Object.is(val, value.current)) {
          value.current = val
          rerender()
        }
      },
      error: (err) => setError(err),
    })
    return () => sub.unsubscribe()
  }, [observable]) // eslint-disable-line react-hooks/exhaustive-deps

  if (error) throw error

  const result = optimistic !== null ? optimistic : value.current
  return [result, setOptimistic]
}

// ---------------------------------------------------------------------------
// useSuspendingLiveQuery — Thin wrapper for Dexie live queries
// ---------------------------------------------------------------------------

/**
 * Observe IndexedDB data with Suspense support and optimistic updates.
 *
 * Returns `[data, setOptimistic]` — call `setOptimistic(newData)` to
 * optimistically override the value until the next real DB emission.
 *
 * @param querier - Dexie query function (auto-tracked by liveQuery)
 * @param cacheKey - Globally unique cache key for observable reuse
 */
export function useSuspendingLiveQuery<T>(
  querier: () => Promise<T> | T,
  cacheKey: React.DependencyList,
): [T, React.Dispatch<React.SetStateAction<T | null>>] {
  return useSuspendingObservable(
    () => Dexie.liveQuery(querier),
    ["dexie", ...cacheKey],
  )
}

// ---------------------------------------------------------------------------
// useLiveQueryImmediate — Non-suspending variant with placeholder
// ---------------------------------------------------------------------------

/**
 * Observe IndexedDB data **without** suspending.
 *
 * Returns `placeholder` on first render (with `isLoading = true`), then
 * swaps to real data when the query resolves (`isLoading = false`).
 *
 * Reuses the same multicast observable cache as `useSuspendingObservable`,
 * so a cache hit returns real data immediately with `isLoading = false`.
 *
 * @param querier - Dexie query function (auto-tracked by liveQuery)
 * @param cacheKey - Globally unique cache key for observable reuse
 * @param placeholder - Value to return while waiting for first emission
 */
export function useLiveQueryImmediate<T>(
  querier: () => Promise<T> | T,
  cacheKey: React.DependencyList,
  placeholder: T,
): [T, React.Dispatch<React.SetStateAction<T | null>>, boolean] {
  const fullKey: React.DependencyList = ["dexie", ...cacheKey]

  // ── Find or create the multicast observable (same logic as suspending) ──
  let observable: Subscribable<T> | undefined
  for (const [key, value] of observableCache) {
    if (
      key.length === fullKey.length &&
      key.every((k, i) => Object.is(k, fullKey[i]))
    ) {
      observable = value as Subscribable<T>
      break
    }
  }

  if (!observable) {
    const source = Dexie.liveQuery(querier)
    let subscription: Unsubscribable | undefined
    const observers = new Set<Observer<T>>()
    let timeout: ReturnType<typeof setTimeout> | undefined

    const newObservable: Subscribable<T> = {
      subscribe: (observer) => {
        observers.add(observer)
        if (timeout != null) {
          clearTimeout(timeout)
          timeout = undefined
        }

        if (!subscription) {
          subscription = source.subscribe({
            next: (val) => {
              valueCache.set(newObservable, val)
              for (const obs of new Set(observers)) obs.next?.(val)
            },
            error: (err) => {
              const lastObservers = new Set(observers)
              handleFinalize()
              for (const obs of lastObservers) obs.error?.(err)
            },
            complete: () => {
              const lastObservers = new Set(observers)
              handleFinalize()
              for (const obs of lastObservers) obs.complete?.()
            },
          })
        } else if (valueCache.has(newObservable)) {
          observer.next?.(valueCache.get(newObservable) as T)
        }

        return {
          unsubscribe: () => {
            if (!observers.has(observer)) return
            observers.delete(observer)
            if (observers.size === 0) {
              scheduleCleanup()
            }
          },
        }

        function handleFinalize() {
          subscription = undefined
          observers.clear()
          valueCache.delete(newObservable)
          promiseCache.delete(newObservable)
          scheduleCleanup()
        }

        function scheduleCleanup() {
          if (timeout != null) return
          timeout = setTimeout(() => {
            if (observers.size > 0) {
              timeout = undefined
              return
            }

            subscription?.unsubscribe()
            subscription = undefined
            for (const [key, value] of observableCache) {
              if (value === newObservable) {
                observableCache.delete(key)
                break
              }
            }
          }, CLEANUP_DELAY)
        }
      },
    }

    observable = newObservable
    observableCache.set(fullKey, newObservable as Subscribable<unknown>)
  }

  // ── Check cache for an existing value ──────────────────────────────────
  const cachedValue = valueCache.has(observable as Subscribable<unknown>)
    ? (valueCache.get(observable as Subscribable<unknown>) as T)
    : undefined
  const hasCachedValue = valueCache.has(observable as Subscribable<unknown>)

  // ── State: current value + loading flag ────────────────────────────────
  const value = React.useRef<T>(hasCachedValue ? cachedValue! : placeholder)
  const [isLoading, setIsLoading] = React.useState(!hasCachedValue)
  const [error, setError] = React.useState<unknown>()
  const rerender = React.useReducer((x) => x + 1, 0)[1]
  const [optimistic, setOptimistic] = React.useState<T | null>(null)

  // Keep value in sync with cache
  if (hasCachedValue && !Object.is(cachedValue, value.current)) {
    value.current = cachedValue!
  }

  // ── Subscribe for live updates ─────────────────────────────────────────
  React.useEffect(() => {
    const sub = observable!.subscribe({
      next: (val) => {
        setOptimistic(null)
        setIsLoading(false)
        if (!Object.is(val, value.current)) {
          value.current = val
          rerender()
        }
      },
      error: (err) => setError(err),
    })
    return () => sub.unsubscribe()
  }, [observable]) // eslint-disable-line react-hooks/exhaustive-deps

  if (error) throw error

  const result = optimistic !== null ? optimistic : value.current
  return [result, setOptimistic, isLoading]
}

// ---------------------------------------------------------------------------
// kickLiveQueries — wake up liveQueries after Dexie Cloud sync
// ---------------------------------------------------------------------------

/**
 * Wake up all Dexie liveQueries that may have missed the `storagemutated`
 * notification after Dexie Cloud's initial sync.
 *
 * **Root cause**: Dexie Cloud's sync opens an `rw` transaction on ALL tables.
 * Each `liveQuery` opens readonly transactions that get blocked until the rw
 * commits. When the rw commits, `storagemutated` fires, but the liveQueries'
 * `shouldNotify()` returns false because `currentObs` hasn't been populated
 * yet (the blocked reads are still resolving). After the reads complete,
 * `accumMuts` is cleared — the sync notification is permanently lost.
 *
 * **Fix**: Dispatch a `x-storagemutated-1` DOM CustomEvent (the same
 * mechanism Dexie uses for cross-connection notification). This causes
 * Dexie's `propagateLocally()` to fire `globalEvents.storagemutated`
 * internally, waking every liveQuery whose `currentObs` overlaps.
 */
export function kickLiveQueries(dbName: string, tableNames: string[]): void {
  if (typeof dispatchEvent === "undefined") return
  const fire = () => {
    try {
      const FULL_RANGE = { d: 1, from: -Infinity, to: [[]] }
      const parts: Record<string, unknown> = {}
      for (const name of tableNames) {
        parts[`idb://${dbName}/${name}`] = FULL_RANGE
      }
      dispatchEvent(new CustomEvent("x-storagemutated-1", { detail: parts }))
    } catch {
      // Non-critical — liveQueries will eventually resolve on their own.
    }
  }
  // Delay to let blocked readonly transactions complete and populate currentObs.
  setTimeout(fire, 150)
  // Safety net: kick again in case some queries were slower to resolve.
  setTimeout(fire, 1500)
}
